import React from 'react';
import { Edit3, Save, X } from 'lucide-react';
import { Permission, roleService, Role } from '../../services/roleService';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import DismissibleError from '../DismissibleError';

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [permissions, setPermissions] = React.useState<Permission[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = React.useState(false);
  const [editingRole, setEditingRole] = React.useState<Role | null>(null);
  const [permissionIds, setPermissionIds] = React.useState<number[]>([]);
  const [saving, setSaving] = React.useState(false);
  const { error, setError, clearError, getErrorMessage } = useErrorHandler();

  const load = React.useCallback(async () => {
    setLoading(true);
    setPermissionsLoaded(false);
    clearError();
    try {
      const [nextRoles, nextPermissions] = await Promise.all([
        roleService.loadRoles(),
        roleService.loadPermissions(),
      ]);
      setRoles(nextRoles);
      setPermissions(nextPermissions);
      setPermissionsLoaded(true);
    } catch (loadError) {
      setError(loadError as any);
    } finally {
      setLoading(false);
    }
  }, [clearError, setError]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const openEditor = (role: Role) => {
    setEditingRole(role);
    setPermissionIds([...(role.permission_ids || [])]);
  };

  const closeEditor = () => {
    setEditingRole(null);
    setPermissionIds([]);
  };

  const togglePermission = (id: number) => {
    setPermissionIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingRole || !permissionsLoaded) return;
    const previous = new Set(editingRole.permission_ids || []);
    const next = new Set(permissionIds);
    const additions = permissions.filter((item) => next.has(item.id) && !previous.has(item.id));
    const removals = permissions.filter((item) => previous.has(item.id) && !next.has(item.id));
    const summary = [
      `Update permissions for ${editingRole.name}?`,
      `Add: ${additions.map((item) => item.name).join(', ') || 'None'}`,
      `Remove: ${removals.map((item) => item.name).join(', ') || 'None'}`,
    ].join('\n');
    if (!window.confirm(summary)) return;

    setSaving(true);
    clearError();
    try {
      await roleService.updateRole(editingRole.id, permissionIds);
      closeEditor();
      await load();
    } catch (saveError) {
      setError(saveError as any);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading canonical roles...</div>;

  return (
    <div className="role-management">
      <div className="page-header">
        <div>
          <h1>Canonical Role and Permission Management</h1>
          <p>The four system roles are protected. Their authorized permission assignments may be adjusted.</p>
        </div>
      </div>

      {error && <DismissibleError message={getErrorMessage()} onDismiss={clearError} onRetry={load} />}

      {editingRole && (
        <div className="modal-overlay">
          <div className="modal" role="dialog" aria-modal="true" aria-label={`Edit ${editingRole.name} permissions`}>
            <div className="modal-header">
              <div>
                <h2>{editingRole.name}</h2>
                <p>Canonical System Role · {editingRole.code}</p>
              </div>
              <button className="close-btn" onClick={closeEditor} aria-label="Close dialog"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="role-form">
              <div className="form-group full-width">
                <label>Authorized permissions</label>
                <div className="permissions-grid">
                  {permissions.map((permission) => (
                    <label key={permission.id}>
                      <input
                        type="checkbox"
                        checked={permissionIds.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                      />
                      <span>{permission.name}<small>{permission.code}</small></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeEditor}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!permissionsLoaded || permissionIds.length === 0 || saving}
                >
                  <Save size={16} />{saving ? 'Saving...' : 'Save Permissions'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="roles-table">
        <table>
          <thead>
            <tr><th>Role</th><th>Code</th><th>Type</th><th>Users</th><th>Permissions</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.id}>
                <td><strong>{role.name}</strong><small>{role.description}</small></td>
                <td>{role.code}</td>
                <td><span className="status default">Canonical System Role</span></td>
                <td>{role.user_count || 0}</td>
                <td>{role.permission_ids?.length || 0}</td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => openEditor(role)} disabled={!permissionsLoaded}>
                    <Edit3 size={13} />Edit Permissions
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RoleManagement;
